// Merkan · Vollbild-Ansicht für Dokumente (PDF, Bilder). Einmal je Oberfläche eingehängt (Rahmen, Kundenportal);
// geöffnet über dokumentZeigen() aus lib/ansicht.ts. Smartphone und Tablet: pdf.js mit Zoom; PC: PDF-Ansicht des Browsers.
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Download, ExternalLink, Minus, Plus, Share2, X } from 'lucide-react';
import { ansicht, anzeigbar, type AnsichtStand } from '../lib/ansicht';
import { herunterladen, kannTeilen, mobilesGeraet, teilenJetzt, type TeilDatei } from '../lib/teilen';
import { t } from '../lib/sprache';
import { PdfSeiten } from './pdf-seiten';
import './dokument-ansicht.css';

const ZOOM = [1, 1.5, 2, 3];

export function DokumentAnsicht() {
  const s = useSyncExternalStore(ansicht.abonnieren, ansicht.stand);
  if (!s) return null;
  return <Fenster key={s.id} s={s} />;
}

function Fenster({ s }: { s: AnsichtStand }) {
  const [datei, setDatei] = useState<TeilDatei | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [zoom, setZoom] = useState(0);
  const mobil = mobilesGeraet();

  useEffect(() => {
    let aktiv = true;
    void s.laden().then(
      (r) => {
        if (!aktiv) return;
        const blob = r instanceof Blob ? r : r.daten;
        const typ = (r instanceof Blob ? '' : r.typ) || s.typ || blob.type || (anzeigbar(null, s.dateiname) === 'pdf' ? 'application/pdf' : 'application/octet-stream');
        // Typ am Blob setzen, damit Browser-Ansicht und Teilen das Format erkennen
        setDatei({ dateiname: s.dateiname, typ, daten: blob.type === typ ? blob : new Blob([blob], { type: typ }) });
      },
      (e) => { if (aktiv) setFehler((e as Error)?.message || t('Die Datei konnte nicht geladen werden.')); },
    );
    return () => { aktiv = false; };
  }, [s]);

  const url = useMemo(() => (datei ? URL.createObjectURL(datei.daten) : null), [datei]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  // Esc schließt, Seite dahinter scrollt nicht mit
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') ansicht.schliessen(); };
    window.addEventListener('keydown', h);
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', h); document.body.style.overflow = vorher; };
  }, []);

  const art = datei ? anzeigbar(datei.typ, datei.dateiname) : anzeigbar(s.typ, s.dateiname);
  // Smartphone und Tablet: immer „Teilen“ mit dem nativen Menü (Sichern in Dateien, Mail, AirDrop …); ohne Teilen-Funktion wird heruntergeladen
  const teilbar = mobil || (datei ? kannTeilen(datei) : false);
  // Direkt im Tipp aufrufen, die Datei liegt bereits vor; so lässt der Browser das Teilen-Menü zu
  const teilen = () => { if (datei) void teilenJetzt(datei, s.titel).then((r) => { if (r === 'freigabe') herunterladen(datei); }); };

  return (
    <div className="da" role="dialog" aria-modal="true" aria-label={s.titel}>
      <header className="da-kopf">
        <button type="button" className="da-knopf" onClick={() => ansicht.schliessen()} aria-label={t('Schließen')} title={t('Schließen')}><X size={20} /></button>
        <div className="da-titel"><strong>{s.titel}</strong><span>{s.dateiname}</span></div>
        {art === 'pdf' && mobil && (
          <div className="da-zoom" role="group" aria-label={t('Zoom')}>
            <button type="button" className="da-knopf" disabled={zoom === 0} onClick={() => setZoom((z) => Math.max(0, z - 1))} aria-label={t('Verkleinern')}><Minus size={18} /></button>
            <button type="button" className="da-knopf" disabled={zoom === ZOOM.length - 1} onClick={() => setZoom((z) => Math.min(ZOOM.length - 1, z + 1))} aria-label={t('Vergrößern')}><Plus size={18} /></button>
          </div>
        )}
        {!mobil && url && <a className="da-knopf" href={url} target="_blank" rel="noreferrer" aria-label={t('In neuem Tab öffnen')} title={t('In neuem Tab öffnen')}><ExternalLink size={18} /></a>}
        {teilbar
          ? <button type="button" className="da-knopf da-haupt" disabled={!datei} onClick={teilen} aria-label={t('Teilen')} title={t('Teilen')}><Share2 size={18} /><span>{t('Teilen')}</span></button>
          : <button type="button" className="da-knopf da-haupt" disabled={!datei} onClick={() => datei && herunterladen(datei)} aria-label={t('Herunterladen')}><Download size={18} /><span>{t('Herunterladen')}</span></button>}
      </header>
      <div className={`da-inhalt${art === 'pdf' && !mobil ? ' da-voll' : ''}`}>
        {fehler && <div className="da-meldung">{fehler}</div>}
        {!fehler && !datei && <div className="da-meldung">{t('Wird geladen …')}</div>}
        {datei && url && art === 'pdf' && (mobil ? <PdfSeiten quelle={datei.daten} zoom={ZOOM[zoom]} titel={s.titel} /> : <iframe src={url} title={s.titel} />)}
        {datei && url && art === 'bild' && <img src={url} alt={s.titel} />}
        {datei && !art && (
          <div className="da-meldung">
            {t('Für diesen Dateityp gibt es keine Vorschau.')}
            <button type="button" className="da-knopf da-haupt" onClick={() => (teilbar ? teilen() : herunterladen(datei))}>{teilbar ? <Share2 size={18} /> : <Download size={18} />}<span>{teilbar ? t('Teilen') : t('Herunterladen')}</span></button>
          </div>
        )}
      </div>
    </div>
  );
}
